import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import { Checkbox } from '../../../components/ui/Checkbox';

const PropertyForm = ({ property, onClose, onSubmit }) => {
  const [formData, setFormData] = useState({
    title: '',
    propertyType: 'Appartement',
    category: 'Vente',
    location: '',
    bedrooms: 0,
    bathrooms: 0,
    area: 0,
    price: 0,
    priceUSD: 0,
    status: 'available',
    featured: false,
    image: '',
    imageAlt: '',
    description: '',
    coordinates: { lat: null, lng: null }
  });

  useEffect(() => {
    if (property) {
      setFormData({
        title: property?.title || '',
        propertyType: property?.propertyType || 'Appartement',
        category: property?.category || 'Vente',
        location: property?.location || '',
        bedrooms: property?.bedrooms || 0,
        bathrooms: property?.bathrooms || 0,
        area: property?.area || 0,
        price: property?.price || 0,
        priceUSD: property?.priceUSD || 0,
        status: property?.status || 'available',
        featured: property?.featured || false,
        image: property?.image || '',
        imageAlt: property?.imageAlt || '',
        description: property?.description || '',
        coordinates: property?.coordinates || { lat: null, lng: null }
      });
    }
  }, [property]);

  const handleSubmit = (e) => {
    e?.preventDefault();
    onSubmit(formData);
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-card rounded-xl shadow-premium border border-border max-w-3xl w-full max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-card border-b border-border p-4 md:p-6 flex items-center justify-between">
          <h2 className="text-xl md:text-2xl font-bold text-foreground">
            {property ? 'Modifier la Propriété' : 'Ajouter une Propriété'}
          </h2>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground">
            <Icon name="X" className="w-6 h-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-4 md:p-6 space-y-4 md:space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Input
              label="Titre"
              value={formData?.title}
              onChange={(e) => setFormData(prev => ({ ...prev, title: e?.target?.value }))}
              required
            />
            <Input
              label="Localisation"
              value={formData?.location}
              onChange={(e) => setFormData(prev => ({ ...prev, location: e?.target?.value }))}
              required
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Select
              label="Type de propriété"
              value={formData?.propertyType}
              onChange={(e) => setFormData(prev => ({ ...prev, propertyType: e?.target?.value }))}
              options={[
                { value: 'Villa', label: 'Villa' },
                { value: 'Appartement', label: 'Appartement' },
                { value: 'Maison', label: 'Maison' },
                { value: 'Bureau', label: 'Bureau' },
                { value: 'Commercial', label: 'Commercial' }
              ]}
            />
            <Select
              label="Catégorie"
              value={formData?.category}
              onChange={(e) => setFormData(prev => ({ ...prev, category: e?.target?.value }))}
              options={[
                { value: 'Vente', label: 'Vente' },
                { value: 'Location', label: 'Location' }
              ]}
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Input
              label="Chambres"
              type="number"
              value={formData?.bedrooms}
              onChange={(e) => setFormData(prev => ({ ...prev, bedrooms: parseInt(e?.target?.value) || 0 }))}
            />
            <Input
              label="Salles de bain"
              type="number"
              value={formData?.bathrooms}
              onChange={(e) => setFormData(prev => ({ ...prev, bathrooms: parseInt(e?.target?.value) || 0 }))}
            />
            <Input
              label="Surface (m²)"
              type="number"
              value={formData?.area}
              onChange={(e) => setFormData(prev => ({ ...prev, area: parseInt(e?.target?.value) || 0 }))}
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Input
              label="Prix (CDF)"
              type="number"
              value={formData?.price}
              onChange={(e) => setFormData(prev => ({ ...prev, price: parseFloat(e?.target?.value) || 0 }))}
              required
            />
            <Input
              label="Prix (USD)"
              type="number"
              value={formData?.priceUSD}
              onChange={(e) => setFormData(prev => ({ ...prev, priceUSD: parseFloat(e?.target?.value) || 0 }))}
            />
          </div>

          <Select
            label="Statut"
            value={formData?.status}
            onChange={(e) => setFormData(prev => ({ ...prev, status: e?.target?.value }))}
            options={[
              { value: 'available', label: 'Disponible' },
              { value: 'rented', label: 'Loué' },
              { value: 'reserved', label: 'Réservé' },
              { value: 'sold', label: 'Vendu' }
            ]}
          />

          <Input
            label="URL de l'image"
            value={formData?.image}
            onChange={(e) => setFormData(prev => ({ ...prev, image: e?.target?.value }))}
            placeholder="https://..."
          />

          <Input
            label="Texte alternatif de l'image"
            value={formData?.imageAlt}
            onChange={(e) => setFormData(prev => ({ ...prev, imageAlt: e?.target?.value }))}
            placeholder="Description de l'image"
          />

          <div>
            <label className="block text-sm font-medium text-foreground mb-2">Description</label>
            <textarea
              value={formData?.description}
              onChange={(e) => setFormData(prev => ({ ...prev, description: e?.target?.value }))}
              className="w-full px-4 py-2 rounded-lg border border-border bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
              rows="4"
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Input
              label="Latitude"
              type="number"
              step="any"
              value={formData?.coordinates?.lat || ''}
              onChange={(e) => setFormData(prev => ({ ...prev, coordinates: { ...prev?.coordinates, lat: parseFloat(e?.target?.value) || null } }))}
              placeholder="6.1319"
            />
            <Input
              label="Longitude"
              type="number"
              step="any"
              value={formData?.coordinates?.lng || ''}
              onChange={(e) => setFormData(prev => ({ ...prev, coordinates: { ...prev?.coordinates, lng: parseFloat(e?.target?.value) || null } }))}
              placeholder="1.2223"
            />
          </div>

          <div className="flex items-center gap-2">
            <Checkbox
              checked={formData?.featured}
              onChange={(e) => setFormData(prev => ({ ...prev, featured: e?.target?.checked }))}
            />
            <label className="text-sm font-medium text-foreground">Propriété en vedette</label>
          </div>

          <div className="flex flex-col sm:flex-row justify-end gap-3 pt-4 border-t border-border">
            <Button type="button" variant="outline" onClick={onClose} className="w-full sm:w-auto">
              Annuler
            </Button>
            <Button type="submit" iconName="Save" iconPosition="left" className="w-full sm:w-auto">
              {property ? 'Modifier' : 'Créer'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default PropertyForm;